import { baseUrl, type ServiceName } from "./services";
import { isMockMode } from "./mock/mode";
import { mockRequest } from "./mock/router";
import { ApiError, type ApiErrorBody } from "./errors";
import { freshToken } from "../auth/refresh";

/**
 * Serverdan fayl oladi (PDF, XLSX).
 *
 * Tokenli so'rovni oddiy havola bilan yuborib bo'lmaydi, shuning
 * uchun fayl `blob` sifatida olinadi.
 */
async function fetchFile(service: ServiceName, path: string, body?: unknown): Promise<Blob> {
  if (isMockMode()) {
    throw new Error("Namuna rejimida fayl yaratilmaydi");
  }
  const token = await freshToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    // Fayl yaratuvchi POST (masalan bo'sh LP yorliqlari) ham
    // idempotent: server kalitsiz so'rovni rad etadi.
    headers["Idempotency-Key"] = crypto.randomUUID();
  }

  const response = await fetch(baseUrl(service) + path, {
    method: body === undefined ? "GET" : "POST",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const envelope = (await response.json().catch(() => undefined)) as { error?: ApiErrorBody } | undefined;
    throw new ApiError(
      response.status,
      envelope?.error ?? { code: "http_error", message: `Fayl olinmadi: HTTP ${response.status}` },
    );
  }
  return response.blob();
}

/** Blobni foydalanuvchi kompyuteriga saqlaydi. */
function saveBlob(blob: Blob, fileName: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 60_000);
}

/**
 * Serverdagi faylni foydalanuvchi kompyuteriga YUKLAB oladi.
 *
 * Hujjatlar va import namunalari uchun. Yorliqlar bu yo'ldan
 * ketmaydi — ular `printFile` bilan to'g'ridan-to'g'ri printerga
 * yuboriladi.
 */
export async function downloadFile(
  service: ServiceName,
  path: string,
  fileName: string,
  body?: unknown,
): Promise<void> {
  saveBlob(await fetchFile(service, path, body), fileName);
}

/** Yashirin ramka yuklanishini kutish muddati. */
const PRINT_LOAD_TIMEOUT_MS = 20_000;

/**
 * Ramka qancha ushlab turiladi.
 *
 * Chop etish dialogi yopilganini brauzer AYTMAYDI (`afterprint`
 * PDF ko'ruvchisidan chiqmaydi), ramka esa dialog ochiqligida
 * o'chirilsa chop etish bekor bo'lardi. Shuning uchun u uzoq
 * muddatga qoldiriladi — ramka bo'sh va ko'rinmaydi.
 */
const PRINT_CLEANUP_MS = 120_000;

/**
 * Blobni yashirin ramkada ochib, chop etish dialogini chaqiradi.
 *
 * Yangi oyna EMAS: `window.open` qalqib chiquvchi oyna to'sig'iga
 * tushadi va xodim nima bo'lmaganini tushunmasdi.
 */
function printBlob(blob: Blob): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText =
      "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none";

    let done = false;
    const cleanup = () => {
      frame.remove();
      URL.revokeObjectURL(url);
    };

    const timer = window.setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Yorliq chop etishga ochilmadi"));
    }, PRINT_LOAD_TIMEOUT_MS);

    frame.onload = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      try {
        const view = frame.contentWindow;
        if (!view) throw new Error("ramka oynasi yo'q");
        view.focus();
        view.print();
      } catch (cause) {
        cleanup();
        reject(cause);
        return;
      }
      window.setTimeout(cleanup, PRINT_CLEANUP_MS);
      resolve();
    };

    frame.onerror = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      cleanup();
      reject(new Error("Yorliq yuklanmadi"));
    };

    frame.src = url;
    document.body.appendChild(frame);
  });
}

/**
 * Yorliqni TO'G'RIDAN-TO'G'RI printerga yuboradi.
 *
 * Ilgari yorliq PDF fayl bo'lib yuklab olinardi va xodim uni
 * qo'lda ochib chop etardi — omborda bu ortiqcha ikki qadam va
 * diskda to'planib qoladigan fayllar degani edi. Endi bitta
 * bosishda tizimning chop etish dialogi ochiladi.
 *
 * Printer tanlovi DIALOGDA qoladi: yorliq printeri va oddiy
 * printer aralashib ketmasligi, nusxa soni ko'rinib turishi
 * kerak.
 *
 * Chop etish ochilmasa (PDF ko'ruvchisi o'chirilgan brauzer)
 * yorliq faylga tushadi — ish TO'XTAMAYDI.
 */
export async function printFile(
  service: ServiceName,
  path: string,
  fileName: string,
  body?: unknown,
): Promise<void> {
  const blob = await fetchFile(service, path, body);
  try {
    await printBlob(blob);
  } catch {
    saveBlob(blob, fileName);
  }
}

/**
 * Faylni `multipart/form-data` bilan yuboradi (import).
 *
 * Fayl SERVERDA o'qiladi (XLSX/CSV, uch tildagi sarlavhalar): brauzerda
 * qayta tahlil qilish ikkinchi, serverdagidan farq qiladigan qoidalar
 * to'plamini paydo qilardi. Xato konverti to'liq qaytadi — rad etilgan
 * paketning `details.batch_id` si xato hisobotiga kerak.
 */
export async function uploadFile<T>(
  service: ServiceName,
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  if (isMockMode()) {
    // Namuna rejimi faylni o'qimaydi — natija shaklini ko'rsatadi.
    return mockRequest<T>(service, path, { method: "POST", body: { file_name: file.name, ...fields } });
  }
  const form = new FormData();
  form.append("file", file, file.name);
  for (const [key, value] of Object.entries(fields)) form.append(key, value);

  const token = await freshToken();
  const headers: Record<string, string> = { Accept: "application/json", "Idempotency-Key": crypto.randomUUID() };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(baseUrl(service) + path, { method: "POST", headers, body: form });
  const parsed = (await response.json().catch(() => undefined)) as
    | { data?: T; error?: ApiErrorBody }
    | undefined;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      parsed?.error ?? { code: "http_error", message: `Import: HTTP ${response.status}` },
      response.headers.get("X-Request-Id") ?? undefined,
    );
  }
  return parsed?.data as T;
}
